import { dropTask } from 'ember-concurrency';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import * as v from 'valibot';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type Owner from '@ember/owner';
import errorsForField from 'tio-common/helpers/errors-for-field';
import RadioGroup from 'tio-ui/components/radio-group';
import Input from 'tio-ui/components/input';
import Select from 'tio-ui/components/select';
import type { Store } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';
import t from 'ember-intl/helpers/t';
import AlertBanner from 'tio-ui/components/alert-banner';
import Popover from 'tio-ui/components/popover';
import type { IntlService } from 'ember-intl';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type AnswerProfileService from 'tio-employee/services/answer-profile';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import Button from 'tio-ui/components/button';
import inputmask from 'tio-common/modifiers/inputmask';
import { getInputmaskUnmaskedValue } from 'tio-common/utils/inputmask';
import {
  answerProfilePrefill,
  buildIdr0726Payloads,
  CURRENT_PLAN_OPTION_KEYS,
  idr0726AnswerProfileUpdates,
  idr0726FormSchema,
  loanInputsFromSummary,
  REQUESTING_APP,
  type Idr0726FormModel,
  type Sf0726LoanInput,
  type Sf0726NsldsPrefill,
} from 'tio-employee/utils/idr-dashboard-0726';

// Editable per-loan card state.
class LoanInputRow {
  loanType: string;
  loanCount: number;
  @tracked currentBalanceDollars: number;
  @tracked interestRatePercent: number;
  @tracked monthlyPaymentDollars: number;
  @tracked originalDisbursementDollars: number;
  @tracked repaymentPlan: string;

  constructor(seed: Sf0726LoanInput) {
    this.loanType = seed.loanType;
    this.loanCount = seed.loanCount;
    this.currentBalanceDollars = seed.currentBalanceDollars;
    this.interestRatePercent = seed.interestRatePercent;
    this.monthlyPaymentDollars = seed.monthlyPaymentDollars;
    this.originalDisbursementDollars = seed.originalDisbursementDollars;
    this.repaymentPlan = seed.repaymentPlan;
  }
}

interface IdrDashboard0726FormSignature {
  Args: {
    user: UserModel;
    loanSummary: LoanSummary;
    nsldsPrefill?: Sf0726NsldsPrefill | null;
    didSave: (recommendation: RecommendationModel) => void;
  };
  Element: HTMLElement;
}

export default class IdrDashboard0726FormComponent extends Component<IdrDashboard0726FormSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare answerProfile: AnswerProfileService;

  @tracked agiDollars = 0;
  @tracked adultsChoice = '';
  @tracked numberOfChildren = 0;
  @tracked firstLoanTime = '';
  @tracked lastLoanTime = '';
  @tracked madePayments = 0;
  @tracked affordPayments = '';
  @tracked hasSubmitted = false;
  @tracked showIncompleteAlert = false;
  @tracked submitError: unknown = null;

  loanRows: LoanInputRow[];

  constructor(owner: Owner, args: IdrDashboard0726FormSignature['Args']) {
    super(owner, args);

    this.loanRows = loanInputsFromSummary(args.loanSummary).map((seed) => new LoanInputRow(seed));

    // Pre-fill from the shared answer profile (only the visible fields).
    const prefill = answerProfilePrefill(args.user?.answerProfile);
    this.agiDollars = prefill.agiDollars;
    this.adultsChoice = prefill.adultsChoice;
    this.numberOfChildren = prefill.numberOfChildren;
    this.firstLoanTime = prefill.firstLoanTime;
    this.lastLoanTime = prefill.lastLoanTime;
    this.madePayments = prefill.madePayments;
    this.affordPayments = prefill.affordPayments;

    // NSLDS-derived cohort answers take precedence.
    const nslds = args.nsldsPrefill;
    if (nslds?.firstLoanTime) {
      this.firstLoanTime = nslds.firstLoanTime;
    }
    if (nslds?.lastLoanTime) {
      this.lastLoanTime = nslds.lastLoanTime;
    }
    if (nslds?.pslfCount != null) {
      this.madePayments = nslds.pslfCount;
    }
  }

  get planOptions() {
    return CURRENT_PLAN_OPTION_KEYS.map((key) => ({
      label: this.intl.t(`strategy_finder_0726.plans.${key}`),
      key,
    }));
  }

  get formModel(): Idr0726FormModel {
    return {
      agi: Math.round(this.agiDollars * 100),
      numberOfAdults: this.adultsChoice ? parseInt(this.adultsChoice, 10) : 0,
      numberOfChildren: this.numberOfChildren,
      firstLoanTime: this.firstLoanTime,
      lastLoanTime: this.lastLoanTime,
      madePayments: this.madePayments,
      affordPayments: this.affordPayments,
    };
  }

  get showCohortNote() {
    const prefill = this.args.nsldsPrefill;
    return !!prefill && !!prefill.firstLoanTime && !!prefill.lastLoanTime;
  }

  get showPslfNote() {
    return this.args.nsldsPrefill?.pslfCount != null;
  }

  get pslfCount() {
    return this.args.nsldsPrefill?.pslfCount ?? 0;
  }

  #formatNsldsDate(value: string | null | undefined) {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat(this.intl.primaryLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }

  get nsldsGeneratedDateLabel() {
    return this.#formatNsldsDate(this.args.nsldsPrefill?.nsldsAsOf);
  }

  get nsldsFirstLoanDateLabel() {
    return this.#formatNsldsDate(this.args.nsldsPrefill?.firstLoanDate);
  }

  get nsldsLastLoanDateLabel() {
    return this.#formatNsldsDate(this.args.nsldsPrefill?.lastLoanDate);
  }

  setMadePayments = (value: string) => {
    this.madePayments = Math.trunc(this.toNumber(value));
  };

  get loanInputs(): Sf0726LoanInput[] {
    return this.loanRows;
  }

  get missingCurrentPlan() {
    return this.loanRows.some((row) => !row.repaymentPlan);
  }

  loanPlanErrors = (row: LoanInputRow) => {
    return this.hasSubmitted && !row.repaymentPlan
      ? [this.intl.t('strategy_finder_0726.current_plan_required')]
      : [];
  };

  get formValidationErrors() {
    try {
      v.parse(idr0726FormSchema, this.formModel);
    } catch (err) {
      if (err instanceof v.ValiError) {
        return err.issues || [];
      }
      return [];
    }
    return [];
  }

  get displayErrors() {
    if (!this.hasSubmitted) {
      return [];
    }
    return this.formValidationErrors.map((issue) => ({
      path: String(issue.path?.[0]?.key ?? ''),
      errors: [issue.message],
      value: '',
    }));
  }

  loanTypeLabel = (loanType: string) => {
    const key = `strategy_finder_0726.loan_types.${loanType}`;
    return this.intl.exists(key) ? this.intl.t(key) : loanType;
  };

  shortLoanTypeLabel = (loanType: string) => {
    const key = `strategy_finder_0726.loan_types_short.${loanType}`;
    return this.intl.exists(key) ? this.intl.t(key) : loanType;
  };

  toNumber = (value: string) => {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  setRowPlan = (row: LoanInputRow, value: string) => {
    row.repaymentPlan = value;
  };

  unmaskedDollars = (value: string, event?: InputEvent) => {
    const unmasked = getInputmaskUnmaskedValue(event);
    const dollars = typeof unmasked === 'number' ? unmasked : Number(unmasked);
    return Number.isFinite(dollars) && unmasked !== '' ? dollars : this.toNumber(value);
  };

  setRowBalance = (row: LoanInputRow, value: string, event?: InputEvent) => {
    row.currentBalanceDollars = this.unmaskedDollars(value, event);
  };

  setRowPayment = (row: LoanInputRow, value: string, event?: InputEvent) => {
    row.monthlyPaymentDollars = this.unmaskedDollars(value, event);
  };

  setRowDisbursed = (row: LoanInputRow, value: string, event?: InputEvent) => {
    row.originalDisbursementDollars = this.unmaskedDollars(value, event);
  };

  setRowRate = (row: LoanInputRow, value: string) => {
    row.interestRatePercent = this.toNumber(value);
  };

  setAgi = (value: string, event?: InputEvent) => {
    this.agiDollars = this.unmaskedDollars(value, event);
  };

  setNumberOfChildren = (value: string) => {
    this.numberOfChildren = Math.trunc(this.toNumber(value));
  };

  async storeAnswersOnProfile(formModel: Idr0726FormModel) {
    try {
      await this.answerProfile.storeAnswers(REQUESTING_APP, idr0726AnswerProfileUpdates(formModel));
    } catch (e) {
      console.error(e);
    }
  }

  submitTask = dropTask(async () => {
    this.submitError = null;
    this.hasSubmitted = true;

    if (
      this.formValidationErrors.length ||
      this.loanInputs.length === 0 ||
      this.missingCurrentPlan
    ) {
      this.showIncompleteAlert = true;
      return;
    }
    this.showIncompleteAlert = false;

    try {
      const formModel = this.formModel;
      const data = buildIdr0726Payloads(formModel, this.loanInputs);
      const recommendation = this.store.createRecord<RecommendationModel>('recommendation', {
        user: this.args.user,
        calculationInput: { data },
        requestingApp: REQUESTING_APP,
      });
      await saveRecord(this.store, recommendation);
      await this.storeAnswersOnProfile(formModel);
      this.hasSubmitted = false;
      this.args.didSave?.(recommendation);
    } catch (e) {
      this.submitError = e;
      console.error(e);
    }
  });

  <template>
    <form data-test-idr-0726-form ...attributes>
      <div class="pb-4">
        <h2 class="tio-text-md-semibold">{{t "strategy_finder_0726.loans_heading"}}</h2>
        <p class="text-xs mt-1 mb-3">{{t "strategy_finder_0726.loans_blurb"}}</p>
        <div class="grid gap-3 md:grid-cols-2">
          {{#each this.loanRows as |row|}}
            <div
              class="tio-bg-surface border tio-border-default rounded-lg p-4"
              data-test-idr-0726-loan-card={{row.loanType}}
            >
              <p class="tio-text-sm-semibold pb-2">
                {{this.shortLoanTypeLabel row.loanType}}
                ({{t "strategy_finder_0726.loan_count" count=row.loanCount}})
              </p>

              <div class="space-y-2 pb-3">
                <div>
                  <label for="balance-{{row.loanType}}" class="form-field-label !text-sm">{{t
                      "strategy_finder_0726.loan_current_balance"
                    }}</label>
                  <Input
                    @onChange={{fn this.setRowBalance row}}
                    @value={{row.currentBalanceDollars}}
                    autocomplete="off"
                    id="balance-{{row.loanType}}"
                    data-test-idr-0726-loan-balance={{row.loanType}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="0"
                      digitsOptional=false
                    }}
                  />
                </div>
                <div>
                  <label for="rate-{{row.loanType}}" class="form-field-label !text-sm">{{t
                      "strategy_finder_0726.loan_interest_rate"
                    }}
                    (%)</label>
                  <Input
                    @onChange={{fn this.setRowRate row}}
                    type="number"
                    @value={{row.interestRatePercent}}
                    autocomplete="off"
                    min="0"
                    step="0.01"
                    id="rate-{{row.loanType}}"
                    data-test-idr-0726-loan-rate={{row.loanType}}
                  />
                </div>
                <div>
                  <label for="payment-{{row.loanType}}" class="form-field-label !text-sm">{{t
                      "strategy_finder_0726.loan_monthly_payment"
                    }}</label>
                  <Input
                    @onChange={{fn this.setRowPayment row}}
                    @value={{row.monthlyPaymentDollars}}
                    autocomplete="off"
                    id="payment-{{row.loanType}}"
                    data-test-idr-0726-loan-payment={{row.loanType}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="0"
                      digitsOptional=false
                    }}
                  />
                </div>
                <div>
                  <label for="disbursed-{{row.loanType}}" class="form-field-label !text-sm">{{t
                      "strategy_finder_0726.loan_disbursed_amount"
                    }}</label>
                  <Input
                    @onChange={{fn this.setRowDisbursed row}}
                    @value={{row.originalDisbursementDollars}}
                    autocomplete="off"
                    id="disbursed-{{row.loanType}}"
                    data-test-idr-0726-loan-disbursed={{row.loanType}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="0"
                      digitsOptional=false
                    }}
                  />
                </div>
              </div>

              <div>
                <label for="plan-{{row.loanType}}" class="form-field-label !text-sm">{{t
                    "strategy_finder_0726.loan_current_plan"
                  }}</label>
                <Select
                  @onChange={{fn this.setRowPlan row}}
                  @items={{this.planOptions}}
                  @selected={{row.repaymentPlan}}
                  @errors={{this.loanPlanErrors row}}
                  @id="plan-{{row.loanType}}"
                  data-test-idr-0726-loan-plan={{row.loanType}}
                />
              </div>
            </div>
          {{/each}}
        </div>
        <AlertBanner @intent="info" class="mt-3" data-test-idr-0726-nslds-note>
          <p class="text-sm">
            {{t "strategy_finder_0726.loans_nslds_note"}}
            <LinkTo
              @route="authenticated.observability.upload"
              @query={{hash providers="nslds" source="strategy_finder"}}
              class="tio-copy"
              data-test-idr-0726-nslds-upload-link
            >{{t "strategy_finder_0726.loans_nslds_link"}}</LinkTo>.
          </p>
        </AlertBanner>
      </div>

      {{#if this.showCohortNote}}
        <AlertBanner @intent="info" class="my-2" data-test-idr-0726-nslds-cohort-note>
          <p class="text-sm">
            {{t
              "strategy_finder_0726.nslds_cohort_note"
              generatedDate=this.nsldsGeneratedDateLabel
              firstLoanDate=this.nsldsFirstLoanDateLabel
              lastLoanDate=this.nsldsLastLoanDateLabel
            }}
            <LinkTo
              @route="authenticated.observability.upload"
              @query={{hash providers="nslds" source="strategy_finder"}}
              class="tio-copy"
              data-test-idr-0726-nslds-cohort-upload-link
            >{{t "strategy_finder_0726.loans_nslds_link"}}</LinkTo>.
          </p>
        </AlertBanner>
      {{/if}}

      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "firstLoanTime" schemaErrors=this.displayErrors}}
        @label={{t "strategy_finder_0726.first_loan_time"}}
        data-test-idr-0726-field="firstLoanTime"
        @onChange={{fn (mut this.firstLoanTime)}}
        @value={{this.firstLoanTime}}
        as |Radio|
      >
        <Radio
          @value="old_borrower"
          @label={{t "strategy_finder_0726.first_loan_time_options.old_borrower"}}
        />
        <Radio
          @value="old_ibr_cohort"
          @label={{t "strategy_finder_0726.first_loan_time_options.old_ibr_cohort"}}
        />
        <Radio
          @value="new_ibr_cohort"
          @label={{t "strategy_finder_0726.first_loan_time_options.new_ibr_cohort"}}
        />
        <Radio
          @value="obbb_cohort"
          @label={{t "strategy_finder_0726.first_loan_time_options.obbb_cohort"}}
        />
      </RadioGroup>

      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "lastLoanTime" schemaErrors=this.displayErrors}}
        @label={{t "strategy_finder_0726.last_loan_time"}}
        data-test-idr-0726-field="lastLoanTime"
        @onChange={{fn (mut this.lastLoanTime)}}
        @value={{this.lastLoanTime}}
        as |Radio|
      >
        <Radio
          @value="pre_obbb_cohort"
          @label={{t "strategy_finder_0726.last_loan_time_options.pre_obbb_cohort"}}
        />
        <Radio
          @value="obbb_cohort"
          @label={{t "strategy_finder_0726.last_loan_time_options.obbb_cohort"}}
        />
      </RadioGroup>

      {{#if this.showPslfNote}}
        <AlertBanner @intent="info" class="my-2" data-test-idr-0726-nslds-pslf-note>
          <p class="text-sm">
            {{t
              "strategy_finder_0726.nslds_pslf_note"
              generatedDate=this.nsldsGeneratedDateLabel
              count=this.pslfCount
            }}
            <LinkTo
              @route="authenticated.observability.upload"
              @query={{hash providers="nslds" source="strategy_finder"}}
              class="tio-copy"
              data-test-idr-0726-nslds-pslf-upload-link
            >{{t "strategy_finder_0726.loans_nslds_link"}}</LinkTo>.
          </p>
        </AlertBanner>
      {{/if}}

      <div class="py-4">
        <label for="madePayments" class="form-field-label !text-sm">
          {{t "strategy_finder_0726.forgiveness.how_many_payments"}}
        </label>
        <Input
          @onChange={{this.setMadePayments}}
          type="number"
          @value={{this.madePayments}}
          autocomplete="off"
          min="0"
          id="madePayments"
          data-test-idr-0726-field="madePayments"
        />
      </div>

      <div class="pt-2 pb-4">
        <div class="pb-3">
          <Popover @triggerMode="hover">
            <:trigger as |pop|>
              <span tabindex="0" {{pop.register}} {{pop.hoverHandlers}}>
                <label for="agi" class="form-field-label !text-sm">
                  {{t "strategy_finder_0726.agi"}}
                </label>
              </span>
            </:trigger>
            <:body>
              {{t "strategy_finder_0726.agi_tooltip" placement="top"}}
            </:body>
          </Popover>
          <Input
            @errors={{errorsForField "agi" schemaErrors=this.displayErrors}}
            @onChange={{this.setAgi}}
            @value={{this.agiDollars}}
            autocomplete="off"
            id="agi"
            {{inputmask
              alias="currency"
              prefix="$"
              unmaskAsNumber=true
              digits="0"
              digitsOptional=false
            }}
          />
        </div>

        <RadioGroup
          data-legacy-input
          class="pb-3"
          @errors={{errorsForField "numberOfAdults" schemaErrors=this.displayErrors}}
          @label={{t "strategy_finder_0726.adults"}}
          data-test-idr-0726-field="adultsChoice"
          @onChange={{fn (mut this.adultsChoice)}}
          @value={{this.adultsChoice}}
          as |Radio|
        >
          <Radio @value="1" @label={{t "strategy_finder_0726.adults_one"}} />
          <Radio @value="2" @label={{t "strategy_finder_0726.adults_two"}} />
        </RadioGroup>

        <div class="pb-3">
          <label for="numberOfChildren" class="form-field-label !text-sm">
            {{t "strategy_finder_0726.children"}}
          </label>
          <Input
            @errors={{errorsForField "numberOfChildren" schemaErrors=this.displayErrors}}
            @onChange={{this.setNumberOfChildren}}
            type="number"
            @value={{this.numberOfChildren}}
            autocomplete="off"
            min="0"
            id="numberOfChildren"
          />
        </div>
      </div>

      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "affordPayments" schemaErrors=this.displayErrors}}
        @label={{t "idr_dashboard_0726.afford_payments"}}
        data-test-idr-0726-field="affordPayments"
        @onChange={{fn (mut this.affordPayments)}}
        @value={{this.affordPayments}}
        as |Radio|
      >
        <Radio @value="yes" @label={{t "common.yes"}} />
        <Radio @value="no" @label={{t "common.no"}} />
      </RadioGroup>

      {{#if this.showIncompleteAlert}}
        <p class="text-sm tio-fg-danger mb-2" role="alert">
          {{t "strategy_finder_0726.please_complete"}}
        </p>
      {{/if}}
      {{#if this.submitError}}
        <AlertBanner @intent="danger" class="mb-4">
          <p class="text-sm">{{t "strategy_finder_0726.submit_error"}}</p>
        </AlertBanner>
      {{/if}}
      <Button
        @intent="primary"
        @isRunning={{this.submitTask.isRunning}}
        data-test-idr-0726-submit
        {{on "click" this.submitTask.perform}}
      >
        {{t "strategy_finder_0726.see_results"}}
      </Button>
    </form>
  </template>
}
